import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import DataGridComponent from '@components/common/DataGridComponent/DataGridComponent'
import { Attachment, Claim } from '@dataTypes/claimFeature.types'
import ClaimAttachmentNote from './ClaimAttachmentNote'
import { getFileIcon } from '@utils/file.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { v4 as uuid } from 'uuid'
import FileViewer from '../FileViewerModal/FileViewer.component'
import {
  attachmentTypeOptions,
  getNotesClaimContextId,
  updateAndCompleteAttachmentsData,
} from '@utils/claims.util'
import type { NoteMutationSuccessDetail } from '@components/common/NoteEditorOverlay/notesFeature.types'
import { INote, NoteContextType } from '@dataTypes/note.type'
import { getAllNotes } from '@services/api/note.api'
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons'
import { useFileSelectionForClaimProcedure } from '@context/FileSelectionForClaimProcedureContext'

const formatOptions = ['PDF', 'JPG', 'PNG', 'DOCX', 'TXT']

function mergeSavedClaimNote(list: INote[], saved: INote): INote[] {
  const id = saved.note_id
  const idx = list.findIndex(
    (n) => n.note_id != null && String(n.note_id) === String(id),
  )
  if (idx >= 0) {
    const next = [...list]
    next[idx] = { ...next[idx], ...saved }
    return next
  }
  return [...list, saved]
}

const matchesClaimContext = (n: INote, contextId: string): boolean => {
  const ct = String(n.context_type ?? '').toLowerCase()
  return (
    ct === NoteContextType.CLAIMS &&
    n.context_id != null &&
    String(n.context_id) === String(contextId)
  )
}

/** Keep local claim notes that the API list omitted (timing / indexing). */
function mergeFetchedClaimNotesWithPrior(
  prior: INote[],
  fetched: INote[],
  contextId: string,
): INote[] {
  const fetchedIds = new Set(
    fetched
      .map((x) => x.note_id)
      .filter((id): id is string => typeof id === 'string' && id.trim() !== '')
      .map((id) => String(id)),
  )
  const extras = prior.filter(
    (n) =>
      matchesClaimContext(n, contextId) &&
      n.note_id != null &&
      String(n.note_id).trim() !== '' &&
      !fetchedIds.has(String(n.note_id)),
  )
  return extras.length > 0 ? [...fetched, ...extras] : fetched
}

type ClaimAttachmentTabProps = {
  selectedClaim: Claim
  onChangeClaim: (updatedClaim: Claim) => void
  isEditingExistingClaim: boolean
}

const ClaimAttachmentTab: React.FC<ClaimAttachmentTabProps> = ({
  selectedClaim,
  onChangeClaim,
  isEditingExistingClaim,
}) => {
  const { openForClaimAttachment } = useFileSelectionForClaimProcedure()
  const [attachments, setAttachments] = useState<Attachment[]>(
    selectedClaim.attachments || [],
  )

  const [editingItemNo, setEditingItemNo] = useState<string | null>(null)
  const [editedAttachment, setEditedAttachment] = useState<Partial<Attachment>>(
    {},
  )
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileViewerInfo, setFileViewerInfo] = useState<{
    title: string
    show: boolean
    file: File
    url: string
  }>({
    title: '',
    show: false,
    file: {} as File,
    url: '',
  })

  const selectedClaimRef = useRef(selectedClaim)
  selectedClaimRef.current = selectedClaim

  const mergeSavedIntoClaimNotes = useCallback(
    (saved: INote): void => {
      const c = selectedClaimRef.current
      onChangeClaim({
        ...c,
        notes: mergeSavedClaimNote(c.notes ?? [], saved),
      })
    },
    [onChangeClaim],
  )

  const handleAttachmentNoteMutationSuccess = useCallback(
    (detail?: NoteMutationSuccessDetail): void => {
      const c = selectedClaimRef.current
      const patientId = c.patientIdSystem ?? c.patientId
      const contextId = getNotesClaimContextId(c)
      const saved = detail?.savedNote

      if (!patientId) {
        if (saved != null) {
          mergeSavedIntoClaimNotes(saved)
        }
        return
      }

      if (!contextId) {
        if (saved != null) {
          mergeSavedIntoClaimNotes(saved)
        }
        return
      }

      const readScoped = async (): Promise<INote[]> => {
        const scoped = await getAllNotes({
          patientId,
          contextType: NoteContextType.CLAIMS,
          contextId,
        })
        if (scoped.statusCode !== 200) return []
        return scoped.data?.notes ?? []
      }

      const readFilteredFallback = async (): Promise<INote[]> => {
        const allPatient = await getAllNotes({ patientId })
        if (allPatient.statusCode !== 200) return []
        return (allPatient.data?.notes ?? []).filter((n) =>
          matchesClaimContext(n, contextId),
        )
      }

      const delay = (ms: number): Promise<void> =>
        new Promise((resolve) => {
          setTimeout(resolve, ms)
        })

      void (async (): Promise<void> => {
        let list = await readScoped()
        if (list.length === 0) {
          list = await readFilteredFallback()
        }

        let needSavedId: string | undefined
        if (saved != null && matchesClaimContext(saved, contextId)) {
          needSavedId = saved.note_id
        }

        if (
          needSavedId != null &&
          !list.some((n) => n.note_id === needSavedId)
        ) {
          for (let attempt = 0; attempt < 2; attempt += 1) {
            await delay(400)
            list = await readScoped()
            if (list.length === 0) {
              list = await readFilteredFallback()
            }
            if (list.some((n) => n.note_id === needSavedId)) break
          }
        }

        if (saved != null && matchesClaimContext(saved, contextId)) {
          if (!list.some((n) => n.note_id === saved.note_id)) {
            list = [...list, saved]
          }
        }

        const prior = selectedClaimRef.current.notes ?? []
        list = mergeFetchedClaimNotesWithPrior(prior, list, contextId)

        const latest = selectedClaimRef.current
        onChangeClaim({ ...latest, notes: list })
      })()
    },
    [mergeSavedIntoClaimNotes, onChangeClaim],
  )

  useEffect(() => {
    setAttachments(selectedClaim.attachments || [])
  }, [selectedClaim])

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    if (!event.target.files) return

    const uploadedFiles = Array.from(event.target.files).map((file, index) => ({
      itemNo: `CA-${attachments.length + index + 1}`,
      item: file.name,
      formatType: file.type.split('/')[1] || 'UNKNOWN',
      orientation: 'Portrait',
      dateTaken: new Date().toISOString().split('T')[0],
      fileName: file.name.replace(/\s+/g, '_'),
      mimeType: file.type,
      size: file.size,
      isEditing: false,
      notes: [],
      newFile: file,
      mapId: uuid(),
    }))

    const updatedAttachments = [...attachments, ...uploadedFiles]
    setAttachments(updatedAttachments)
    onChangeClaim({
      ...selectedClaim,
      attachments: updatedAttachments,
    })
  }

  const handleChange = (
    field: keyof Attachment,
    value: string,
    itemNo?: string,
  ): void => {
    setEditedAttachment((prev) => ({ ...prev, [field]: value }))
    const updatedAttachments = attachments.map((att) =>
      att.itemNo === itemNo ? { ...att, [field]: value } : att,
    )

    setAttachments(updatedAttachments)
    onChangeClaim({ ...selectedClaim, attachments: updatedAttachments })
  }

  const handleSave = (): void => {
    const updatedAttachments = attachments.map((att) =>
      att.itemNo === editingItemNo ? { ...att, ...editedAttachment } : att,
    )

    setAttachments(updatedAttachments)
    onChangeClaim({ ...selectedClaim, attachments: updatedAttachments })
    setEditingItemNo(null)
  }

  const handleDelete = (itemNo: string): void => {
    const updatedAttachments = attachments.filter(
      (att) => att.itemNo !== itemNo,
    )
    setAttachments(updatedAttachments)
    onChangeClaim({ ...selectedClaim, attachments: updatedAttachments })
  }

  const handleViewAttachment = (attachment: Attachment): void => {
    const hasFile = attachment.newFile != null
    const hasUrl = attachment.fileUrl != null
    if (!hasFile && !hasUrl) return
    setFileViewerInfo({
      title: `Viewing: ${attachment.item ?? 'Attachment'}`,
      show: true,
      file: attachment.newFile ?? ({} as File),
      url: attachment.fileUrl ?? '',
    })
  }

  const handleEditAttachment = (rowData: Attachment): void => {
    const isAlreadyEditing = editingItemNo === rowData.itemNo
    setEditingItemNo(isAlreadyEditing ? null : rowData.itemNo)
    setEditedAttachment(isAlreadyEditing ? {} : { ...rowData })
  }

  const formatDate = (dateString: string): string =>
    new Date(dateString).toLocaleDateString()

  const columns = useMemo(
    () => [
      { field: 'itemNo', headerText: 'Item #', width: '70px' },
      {
        field: 'item',
        headerText: 'Item',
        width: '70px',
        template: (rowData: Attachment): JSX.Element => (
          <div className="file-icon-wrapper" title={rowData.item}>
            <i className={`${getFileIcon(rowData.item)} file-icon fs-2`} />
          </div>
        ),
      },
      {
        field: 'formatType',
        headerText: 'Format type',
        width: '100px',
        template: (rowData: Attachment): JSX.Element =>
          editingItemNo === rowData.itemNo ? (
            <select
              value={editedAttachment.formatType || rowData.formatType}
              onChange={(e) =>
                handleChange('formatType', e.target.value, rowData.itemNo)
              }
              className="form-select"
            >
              {formatOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : (
            <span>{rowData.formatType.toString().toUpperCase()}</span>
          ),
      },
      {
        field: 'attachmentType',
        headerText: 'Attachment type',
        width: '200px',
        template: (rowData: Attachment): JSX.Element => (
          <select
            value={rowData.attachmentType}
            onChange={(e) =>
              handleChange('attachmentType', e.target.value, rowData.itemNo)
            }
            className="form-select"
          >
            <option value="">Select attachment type</option>
            {attachmentTypeOptions
              .filter((option) => !['03', 'RB', 'P6'].includes(option.value))
              .sort(
                (a, b) =>
                  ['B4', 'DA', 'DG', 'EB', 'RR', 'XP', 'OZ'].indexOf(a.value) -
                  ['B4', 'DA', 'DG', 'EB', 'RR', 'XP', 'OZ'].indexOf(b.value),
              )
              .map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
          </select>
        ),
      },
      {
        field: 'dateTaken',
        headerText: 'Date taken',
        width: '80px',
        template: (rowData: Attachment): JSX.Element => (
          <span>{formatDate(rowData.dateTaken)}</span>
        ),
      },
      {
        field: 'actions',
        headerText: 'Actions',
        width: '70px',
        template: (rowData: Attachment): JSX.Element => (
          <div className="action-buttons-container">
            {editingItemNo === rowData.itemNo && (
              <button
                type="button"
                title="Save"
                className="action-button"
                onClick={handleSave}
              >
                <i className="ti ti-check text-success" />
              </button>
            )}
            <ActionButtons
              onView={() => handleViewAttachment(rowData)}
              onEdit={() => handleEditAttachment(rowData)}
              onDelete={() => handleDelete(rowData.itemNo)}
              showMoreOptions={false}
            />
          </div>
        ),
      },
    ],
    [attachments, editingItemNo, editedAttachment],
  )

  return (
    <div>
      <div className="text-end mb-2">
        <input
          type="file"
          multiple
          ref={fileInputRef}
          className="hidden-file-input"
          onChange={handleFileUpload}
          accept=".pdf,.png,.jpg,.jpeg,.gif"
        />
        <DropDownButtonComponent
          items={[
            {
              text: 'Local device',
              iconCss: 'mgc_device_line',
            },
            {
              text: 'Cloud storage',
              iconCss: 'mgc_cloud_line',
            },
          ]}
          cssClass="upload-source-dropdown e-caret-hide act-button"
          select={(args) => {
            if (args.item.text === 'Local device') {
              fileInputRef.current?.click()
            } else if (args.item.text === 'Cloud storage') {
              openForClaimAttachment(async (selectedAttachments) => {
                const updatedAttachments =
                  await updateAndCompleteAttachmentsData(selectedAttachments)
                const updated = [
                  ...attachments,
                  ...updatedAttachments,
                ] as Attachment[]
                setAttachments(updated)
                onChangeClaim({ ...selectedClaim, attachments: updated })
              }, attachments.length)
            }
          }}
        >
          <i className="ti ti-upload me-1"></i>{' '}
          <span className="upload-source-text" style={{ fontSize: '0.875rem' }}>
            Upload
          </span>
        </DropDownButtonComponent>
      </div>

      <DataGridComponent
        key={attachments.length}
        data={attachments}
        columns={columns}
        customCSS="claim-attachment-table"
        showCheckbox={false}
      />

      <ClaimAttachmentNote
        selectedClaim={selectedClaim}
        isEditingExistingClaim={isEditingExistingClaim}
        attachmentNotes={selectedClaim.notes || []}
        onUpdateNotes={(newNotes) => {
          onChangeClaim({ ...selectedClaim, notes: newNotes })
        }}
        onNoteSavedImmediate={mergeSavedIntoClaimNotes}
        onNoteMutationSuccess={handleAttachmentNoteMutationSuccess}
      />

      {fileViewerInfo.show && (
        <FileViewer
          show={fileViewerInfo.show}
          onHide={() => setFileViewerInfo({ ...fileViewerInfo, show: false })}
          title={fileViewerInfo.title}
          {...(fileViewerInfo.file && { sourcefile: fileViewerInfo.file })}
          {...(fileViewerInfo.url && { sourceUrl: fileViewerInfo.url })}
        />
      )}
    </div>
  )
}

export default ClaimAttachmentTab
